import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { toast } from "sonner";

export type TakeoffOwnerType = "estimate" | "project";

export interface Annotation {
  id: string;
  project_document_id: string | null;
  estimate_document_id: string | null;
  company_id: string;
  created_by: string;
  page_number: number;
  annotation_type: string;
  geometry: any;
  label: string | null;
  color: string;
  measured_value: number | null;
  unit: string;
  takeoff_group_id: string | null;
  source_estimate_annotation_id: string | null;
  created_at: string;
}

export interface DocumentScale {
  id: string;
  project_document_id: string | null;
  estimate_document_id: string | null;
  page_number: number;
  pixels_per_unit: number;
  unit: string;
  created_by: string;
}

/**
 * Fetch annotations for a document (works for both project and estimate documents).
 * The documentId is the row ID from either project_documents or estimate_documents.
 */
export function useAnnotations(
  documentId: string,
  pageNumber: number,
  ownerType: TakeoffOwnerType = "project"
) {
  const column = ownerType === "estimate" ? "estimate_document_id" : "project_document_id";

  return useQuery({
    queryKey: ["document_annotations", documentId, pageNumber],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_annotations")
        .select("*")
        .eq(column, documentId)
        .eq("page_number", pageNumber)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as Annotation[];
    },
    enabled: !!documentId,
  });
}

export function useAllAnnotationCounts(documentIds: string[], ownerType: TakeoffOwnerType = "project") {
  const column = ownerType === "estimate" ? "estimate_document_id" : "project_document_id";

  return useQuery({
    queryKey: ["annotation_counts", documentIds, ownerType],
    queryFn: async () => {
      if (documentIds.length === 0) return {};
      const { data, error } = await supabase
        .from("document_annotations")
        .select(column)
        .in(column, documentIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((r: any) => {
        const docId = r[column];
        counts[docId] = (counts[docId] || 0) + 1;
      });
      return counts;
    },
    enabled: documentIds.length > 0,
  });
}

export function useSaveAnnotation() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { activeCompanyId } = useCompanySelector();

  return useMutation({
    mutationFn: async (ann: {
      documentId: string;
      ownerType: TakeoffOwnerType;
      page_number: number;
      annotation_type: string;
      geometry: any;
      label?: string;
      color?: string;
      measured_value?: number;
      unit?: string;
      takeoff_group_id?: string | null;
    }) => {
      const insertObj: any = {
        company_id: activeCompanyId!,
        created_by: user!.id,
        page_number: ann.page_number,
        annotation_type: ann.annotation_type,
        geometry: ann.geometry,
        label: ann.label || null,
        color: ann.color || "#FF0000",
        measured_value: ann.measured_value ?? null,
        unit: ann.unit || "ft",
        takeoff_group_id: ann.takeoff_group_id ?? null,
      };

      if (ann.ownerType === "estimate") {
        insertObj.estimate_document_id = ann.documentId;
      } else {
        insertObj.project_document_id = ann.documentId;
      }

      const { error } = await supabase.from("document_annotations").insert(insertObj);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["document_annotations", vars.documentId, vars.page_number] });
      qc.invalidateQueries({ queryKey: ["annotation_counts"] });
    },
    onError: (err: any) => toast.error(err.message),
  });
}

export function useDeleteAnnotation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (ann: { id: string; documentId: string; page_number: number }) => {
      const { error } = await supabase
        .from("document_annotations")
        .delete()
        .eq("id", ann.id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["document_annotations", vars.documentId, vars.page_number] });
      qc.invalidateQueries({ queryKey: ["annotation_counts"] });
      toast.success("Annotation deleted");
    },
    onError: (err: any) => toast.error(err.message || "Could not delete — you may not have permission"),
  });
}

export function useUpdateAnnotation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (ann: {
      id: string;
      documentId: string;
      page_number: number;
      geometry?: any;
      color?: string;
      label?: string | null;
      takeoff_group_id?: string | null;
    }) => {
      const updates: Record<string, any> = {};
      if (ann.geometry !== undefined) updates.geometry = ann.geometry;
      if (ann.color !== undefined) updates.color = ann.color;
      if (ann.label !== undefined) updates.label = ann.label;
      if (ann.takeoff_group_id !== undefined) updates.takeoff_group_id = ann.takeoff_group_id;
      const { error } = await supabase
        .from("document_annotations")
        .update(updates)
        .eq("id", ann.id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["document_annotations", vars.documentId, vars.page_number] });
    },
    onError: (err: any) => toast.error(err.message),
  });
}

export function useDocumentScale(
  documentId: string,
  pageNumber: number,
  ownerType: TakeoffOwnerType = "project"
) {
  const column = ownerType === "estimate" ? "estimate_document_id" : "project_document_id";

  return useQuery({
    queryKey: ["document_scale", documentId, pageNumber],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_scales")
        .select("*")
        .eq(column, documentId)
        .eq("page_number", pageNumber)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as DocumentScale | null;
    },
    enabled: !!documentId,
  });
}

export function useSaveScale() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { activeCompanyId } = useCompanySelector();

  return useMutation({
    mutationFn: async (scale: {
      documentId: string;
      ownerType: TakeoffOwnerType;
      page_number: number;
      pixels_per_unit: number;
      unit?: string;
    }) => {
      const upsertObj: any = {
        company_id: activeCompanyId!,
        page_number: scale.page_number,
        pixels_per_unit: scale.pixels_per_unit,
        unit: scale.unit || "ft",
        created_by: user!.id,
      };

      if (scale.ownerType === "estimate") {
        upsertObj.estimate_document_id = scale.documentId;
      } else {
        upsertObj.project_document_id = scale.documentId;
      }

      // Delete any existing scale for this document+page, then insert fresh.
      // This replaces the upsert approach which failed because the DB only has
      // partial unique indexes (not full constraints) on the conflict columns.
      const fkColumn = scale.ownerType === "estimate"
        ? "estimate_document_id"
        : "project_document_id";

      await supabase
        .from("document_scales")
        .delete()
        .eq(fkColumn, scale.documentId)
        .eq("page_number", scale.page_number);

      const { error } = await supabase
        .from("document_scales")
        .insert(upsertObj);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["document_scale", vars.documentId, vars.page_number] });
      toast.success("Scale calibrated");
    },
    onError: (err: any) => toast.error(err.message),
  });
}
